import { Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { appInjector } from '../app-injector';
import * as constants from '../constants-types/constants';
import {
    GlobalEmailTypes,
    SurveyUserTypes,
    AssessmentApiProblems,
    ExternalAssessmentStates
} from '../constants-types/Enums';
import { InvitationSource } from '../core-data-model/Assessment.model';
import { AssessmentDetail } from '../core-data-model/AssessmentDetails.model';
import { OiLink } from '../core-data-model/OiLink.model';
import { Person } from '../core-data-model/Person.model';
import { BehavioralAssessmentDetailsServer } from '../core-data-model/PersonApi.serverinterface';
import { ScheduledAssessmentDetails } from '../core-data-model/ScheduledAssessmentDetails.model';
import { AssessmentsApi } from '../core-services/AssessmentsApi.connect';
import { CompaniesApi } from '../core-services/CompaniesApi.connect';
import { ContextDataService } from '../core-services/Context.dataservice';
import { JobApi } from '../core-services/JobApi/JobApi.connect';
import { JobFactory } from '../core-services/JobFactory.service';
import { notification } from '../core-services/Notifications.service';
import { PersonApi } from '../core-services/PersonApi.connect';
import { ScheduledAssessmentDetailsFactory } from '../core-services/ScheduledAssessmentDetails.factory';
import {
    onlySuccess,
    OnlySuccessReturn
} from '../core-utilities/AsyncDecorator';
import { StringUtilities } from '../core-utilities/String.utility';
import { ConfirmationHelpers } from '../modals/Confirmation.decorator';
import { UserProfileSettingApi } from '../system-services/UserProfileSettings/UserProfileSetting.connect';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { TranslationService } from '../translations';
import { CompanySettingsApi } from './CompanySettingsApi';

export enum AssessmentCenterTabs {
    InviteByEmail = 0,
    InviteByLink = 1,
    AddByBehavioralScoreID = 2,
    Checklist = 3,
    Bulk = 4
}

interface AssessmentTypeAction {
    modifyPerson: (person: Person) => void;
    confirmAndChangePersonType: (
        data: ScheduledAssessmentDetails
    ) => Promise<void>;
}

@Injectable({
    providedIn: 'root'
})
export class AssessmentCenterService {
    editingAssessmentLink: OiLink = null;
    checkListAvailableForDownload = true;

    activeTabNumber: AssessmentCenterTabs = AssessmentCenterTabs.InviteByEmail;
    _data: ScheduledAssessmentDetails;
    dataPromise: Promise<ScheduledAssessmentDetails>;

    behavioralEmailSubject: string;
    behavioralEmailMessage: string;
    // What started the send assessment flow
    private personTypeStrategy: AssessmentTypeAction = new NoTypeAction();

    constructor(
        private companiesApi: CompaniesApi,
        private personApi: PersonApi,
        private contextService: ContextDataService,
        private router: Router,
        private TR: TranslationService,
        private userProfileSettingApi: UserProfileSettingApi,
        private userProfile: UserProfileSettingsService,
        private assessmentsApi: AssessmentsApi,
        private jobApi: JobApi,
        private scheduledAssessmentDetailsFactory: ScheduledAssessmentDetailsFactory,
        private route: ActivatedRoute,
        private jobFactory: JobFactory,
        private companySettings: CompanySettingsApi
    ) {
        this.getCompanySettings();
    }

    get data() {
        if (this._data) {
            return this._data;
        } else if (!this.dataPromise) {
            this.dataPromise = this.scheduledAssessmentDetailsFactory
                .makeScheduledAssessmentDetails()
                .then((data) => {
                    this._data = data;
                    return data;
                });
        }
    }
    clearState(clearContext = true): void {
        this.personTypeStrategy = new NoTypeAction();
        this._data = null;
        this.dataPromise = null;

        if (clearContext) {
            this.contextService.updateJobForRecipients = null;
            this.contextService.clearResultSet();
        }
    }

    setEditingAssessmentLink(workOnThisOiLink: OiLink) {
        this.editingAssessmentLink = workOnThisOiLink;
    }

    fetchEmailSettings(
        emailType: GlobalEmailTypes = GlobalEmailTypes.BehavioralAssessmentEmail
    ) {
        this.companySettings
            .fetchEmailSettings({
                emailType: GlobalEmailTypes[emailType]
            })
            .subscribe((response) => {
                if (
                    response.isSuccess &&
                    emailType === GlobalEmailTypes.BehavioralAssessmentEmail
                ) {
                    this.behavioralEmailSubject = response.data.subject;
                    this.behavioralEmailMessage = response.data.message;
                }
            });
    }

    async sendEmail(
        autoSendBehavioralReport = false,
        invitationSource: InvitationSource = InvitationSource.Unknown,
        skipNotifyAssessment = false,
        expirationDateOverride: Date,
        skipNotifyAnalystCompletionEmail = false
    ): Promise<OnlySuccessReturn[]> {
        // NOTE: This is duplicating the state. Ideally, we should have a single source of
        // truth in either the context service or the sendAssessmentFlow. Not both.

        // This hack is required because we are trying async code like synchronous code in the data() getter above
        // which needs to be refactored
        const sendEmailData = this.data ? this.data : await this.dataPromise;

        this.contextService.selectedResultSetPeople.forEach((p) => {
            this.addAssessment(p);
        });

        this.personTypeStrategy.confirmAndChangePersonType(sendEmailData);

        if (
            sendEmailData.sendCognitive ||
            sendEmailData.sendCognitiveAfterBehavioralIsComplete
        ) {
            await this.validateCogBestPracticeAcknowledged();
        }
        const retval = await this.sendPersonAssessmentInternal(
            autoSendBehavioralReport,
            invitationSource,
            skipNotifyAssessment,
            expirationDateOverride,
            skipNotifyAnalystCompletionEmail
        );
        this.clearState();
        return retval;
    }

    assessmentCompleted(assessmentState: ExternalAssessmentStates): boolean {
        return (
            assessmentState === ExternalAssessmentStates.Completed ||
            assessmentState === ExternalAssessmentStates.CompleteEmailSent ||
            assessmentState ===
                ExternalAssessmentStates.AssessmentCompleteWebHookCalled ||
            assessmentState ===
                ExternalAssessmentStates.AssessmentCompleteEmailReceived ||
            assessmentState ===
                ExternalAssessmentStates.AssessmentCompleteWebHookComplete
        );
    }

    private getCompanySettings() {
        this.companiesApi.getGeneralSettings().then((settings) => {
            this.checkListAvailableForDownload =
                constants.checkListDownloadFormIdsSupported.includes(
                    settings.formId
                );
        });
    }

    private async validateCogBestPracticeAcknowledged(): Promise<void> {
        if (
            !this.userProfile.userProfileSetting.hasAcknowledgedCogBestPractices // cog best practice not acknowledged
        ) {
            await ConfirmationHelpers.generateSingleChoiceConfirmationModal(
                this.TR.key('cognitiveBestPracticeAcknowledgement'),
                {
                    yes: 'text.common-terms.continue'
                }
            );
            this.userProfileSettingApi.setUserAcknowledgedCogBestPractices(
                true
            );
            this.userProfile.setUserProfileSetting();
            this.userProfile.userProfileSetting.hasAcknowledgedCogBestPractices = true;
        }
    }

    private addAssessment(personToAdd: Person): AssessmentDetail {
        const job =
            personToAdd.job && personToAdd.job.title
                ? this.jobFactory.newJob(personToAdd.job)
                : null;

        const assessmentDetails = new AssessmentDetail(personToAdd, job);
        this.personTypeStrategy.modifyPerson(personToAdd);
        this.data.assessments.push(assessmentDetails);
        return assessmentDetails;
    }

    /**
     * @deprecated
     */
    private resendBehavioralAssessment(assessmentId: string) {
        return this.assessmentsApi.updateAssessmentAndResend(
            assessmentId,
            this.behavioralEmailSubject,
            this.behavioralEmailMessage
        );
    }

    /**
     * @deprecated
     */
    private sendBehavioralAssessment(
        data: ScheduledAssessmentDetails,
        autoSendBehavioralReport: boolean,
        invitationSource: InvitationSource = InvitationSource.Unknown,
        skipNotifyAssessment = false,
        expirationDateOverride: Date,
        skipNotifyAnalystCompletionEmail = false
    ) {
        return this.assessmentsApi.sendBehavioralAssessment(
            data,
            invitationSource,
            skipNotifyAssessment,
            data.sendCognitiveAfterBehavioralIsComplete,
            skipNotifyAnalystCompletionEmail,
            expirationDateOverride,
            autoSendBehavioralReport
        );
    }

    private getSyncedCustomEmailFields() {
        if (
            this.data.sendBehavioral &&
            this.behavioralEmailSubject &&
            this.behavioralEmailMessage
        ) {
            this.data.behavioralEmail.subject = this.behavioralEmailSubject;
            this.data.behavioralEmail.message = this.behavioralEmailMessage;
        }
    }

    private sendCognitiveAssessment(data: ScheduledAssessmentDetails) {
        return this.assessmentsApi.sendCognitiveAssessment(data);
    }

    private sendPersonAssessmentInternal(
        autoSendBehavioralReport: boolean,
        invitationSource?: InvitationSource,
        skipNotifyAssessment = false,
        expirationDateOverride?: Date,
        skipNotifyAnalystCompletionEmail = false
    ): Promise<OnlySuccessReturn[]> {
        const arrayOfPromises: Promise<any>[] = [];
        this.getSyncedCustomEmailFields();
        // Send BOTH Behavioral and Cognitive Assessment
        if (this.data.sendBehavioral && this.data.sendCognitive) {
            arrayOfPromises.push(
                onlySuccess(
                    this.sendBehavioralAssessment(
                        this.data,
                        autoSendBehavioralReport,
                        invitationSource,
                        skipNotifyAssessment,
                        expirationDateOverride,
                        skipNotifyAnalystCompletionEmail
                    ).then(([apiResults]) => {
                        if (!this.data.sendCognitiveAfterBehavioralIsComplete) {
                            return this.sendCognitiveAssessment(
                                this.updateWithIds(this.data, apiResults)
                            );
                        }
                    }),
                    AssessmentApiProblems.SendingCognitveAndBehavior
                )
            );

            // Send ONLY Behavioral Assessment
        } else if (this.data.sendBehavioral) {
            // First take care of resending an assessment if there is one
            if (this.data.resendBehavioralAssessmentId) {
                arrayOfPromises.push(
                    onlySuccess(
                        this.resendBehavioralAssessment(
                            this.data.resendBehavioralAssessmentId
                        ),
                        AssessmentApiProblems.SendingBehavioral
                    )
                );
            }
            // Then send the rest of the non-resend behavioral assessments
            // if there are any.
            else if (this.data.assessments.length) {
                arrayOfPromises.push(
                    onlySuccess(
                        this.sendBehavioralAssessment(
                            this.data,
                            autoSendBehavioralReport,
                            invitationSource,
                            skipNotifyAssessment,
                            expirationDateOverride,
                            skipNotifyAnalystCompletionEmail
                        ),
                        AssessmentApiProblems.SendingBehavioral
                    )
                );
            }

            // Send ONLY Cognitive Assessment
        } else if (this.data.sendCognitive) {
            arrayOfPromises.push(
                onlySuccess(
                    this.sendCognitiveAssessment(this.data),
                    AssessmentApiProblems.SendingCognitive
                )
            );
        }

        return this.processPromises(arrayOfPromises);
    }

    private updateWithIds(
        coreData: ScheduledAssessmentDetails,
        fromServer: BehavioralAssessmentDetailsServer[]
    ) {
        coreData.assessments.forEach((assessment: AssessmentDetail) => {
            assessment.person.id = fromServer.find(
                (response) =>
                    assessment.person.firstName === response.firstName &&
                    assessment.person.lastName === response.lastName &&
                    assessment.person.email === response.email
            ).assessmentUserId;
        });
        return coreData;
    }

    private processPromises(arrayOfPromises: Promise<OnlySuccessReturn>[]) {
        return Promise.all(arrayOfPromises).then((arrayOfResults) => {
            const hashOfErrors = new Map<
                keyof typeof AssessmentApiProblems,
                boolean
            >();
            const failures = arrayOfResults.filter(
                (result) => result.status === 'rejected'
            );
            if (failures.length > 0) {
                failures.forEach((failure) => {
                    hashOfErrors.set(
                        AssessmentApiProblems[
                            failure.tag
                        ] as keyof typeof AssessmentApiProblems,
                        true
                    );
                });
                throw failures;
            }
            return arrayOfResults;
        });
    }

    // Methods to initialize State and Navigate to AssessmentCenter

    async startAssessCandidatesFromJobId(jobId = ''): Promise<any> {
        this.contextService.updateJobForRecipients =
            await this.jobApi.getJob(jobId);
        this.personTypeStrategy = new CandidatesTypeAction();
        this.router.navigate(['/assessmentCenter'], {
            relativeTo: this.route
        });
    }

    async openSendAssesmentForMultiplePeople(
        people: Person[],
        emailType: GlobalEmailTypes
    ) {
        people
            .filter((p) => p.folder && p.email)
            .forEach((p) => this.contextService.addToResultSet(p));
        this.dataPromise =
            this.scheduledAssessmentDetailsFactory.makeScheduledAssessmentDetails(
                this.getSettings(emailType)
            );
        this._data = await this.dataPromise;

        this.router.navigate(['assessmentCenter']);
    }

    async openSendAssessment(personId: string, emailType: GlobalEmailTypes) {
        this.dataPromise =
            this.scheduledAssessmentDetailsFactory.makeScheduledAssessmentDetails(
                this.getSettings(emailType)
            );
        let people;
        [this._data, people] = await Promise.all([
            this.dataPromise,
            this.personApi.getPersonDetails([personId])
        ]);
        this.contextService.addToResultSet(people[0]);
        this.router.navigate(['assessmentCenter']);
    }

    @notification('notification.title-success')
    async openResendAssessment(
        emailType: GlobalEmailTypes,
        assessmentId: string
    ) {
        this.dataPromise =
            this.scheduledAssessmentDetailsFactory.makeScheduledAssessmentDetails(
                Object.assign(
                    {
                        resendBehavioralAssessmentId: assessmentId
                    },
                    this.getSettings(emailType)
                )
            );
        this._data = await this.dataPromise;
        const retval = await this.sendPersonAssessmentInternal(false);
        this.clearState(false);
        return retval;
    }

    private getSettings(emailType: GlobalEmailTypes) {
        return emailType === GlobalEmailTypes.BehavioralAssessmentEmail
            ? {
                  sendBehavioral: true,
                  sendCognitive: false,
                  sendCognitiveAfterBehavioralIsComplete: false
              }
            : {
                  sendBehavioral: false,
                  sendCognitive: true,
                  sendCognitiveAfterBehavioralIsComplete: false
              };
    }
}

class CandidatesTypeAction implements AssessmentTypeAction {
    stringUtil: StringUtilities;
    TR: TranslationService;

    constructor() {
        this.stringUtil = appInjector().get(StringUtilities);
        this.TR = appInjector().get(TranslationService);
    }

    modifyPerson(person: Person) {
        person.type = SurveyUserTypes.Candidate;
    }

    async confirmAndChangePersonType(data: ScheduledAssessmentDetails) {
        const nonCandidateCount = this.getPeopleWithWrongType(data).length;
        if (nonCandidateCount) {
            const confirmationText = this.stringUtil.formatString(
                this.TR.key('dataCleanup.convertToCandidate-multiple'),
                nonCandidateCount
            );
            await ConfirmationHelpers.generateConfirmationModal(
                confirmationText,
                null,
                {
                    yes: 'dataCleanup.confirmation-yes',
                    no: 'dataCleanup.confirmation-no'
                }
            );
        }
        data.assessments.forEach((assessment) => {
            assessment.person.type = SurveyUserTypes.Candidate;
        });
    }

    private getPeopleWithWrongType(data: ScheduledAssessmentDetails): Person[] {
        return data.assessments
            .filter((item) => item.person.type !== SurveyUserTypes.Candidate)
            .map((item) => item.person);
    }
}

class NoTypeAction implements AssessmentTypeAction {
    modifyPerson(_person: Person) {
        // Implementation left intentionally blank
    }
    async confirmAndChangePersonType(_data: ScheduledAssessmentDetails) {
        // Implementation left intentionally blank
    }
}
